import { isValidCourseId, canonicalizeCourseId } from './courseContext';

/**
 * Reads the `effectiveEntitlement` summary that GET /users/me returns.
 *
 * A student's entitlement lives on their PARENT's account. The app used to fetch
 * GET /users/:parentId and evaluate the parent's raw `subscriptions` array in the
 * browser, which handed every student their parent's billing document (STUD-632).
 * The server now answers the question directly and this reads that answer.
 *
 * @param {object} summary - response.data.effectiveEntitlement
 * @param {string} activeCourseId - the currently selected course, if any
 * @returns {{subscriptionActive: boolean, courseIdToActivate: string|null}}
 */
export function resolveEntitlementFromProfile(summary, activeCourseId) {
    // A missing or malformed summary means a new client is talking to an older backend,
    // or the response was truncated. Fail closed: the server gate is authoritative
    // either way, so guessing "entitled" here would only produce a broken UI.
    if (!summary || typeof summary !== 'object') {
        return { subscriptionActive: false, courseIdToActivate: null };
    }

    const subscriptionActive = summary.entitled === true;

    // An already-selected course is never overridden — the user (or an earlier bootstrap)
    // chose it, and silently switching courses underneath them would be worse than
    // leaving a stale selection.
    if (isValidCourseId(activeCourseId)) {
        return { subscriptionActive, courseIdToActivate: null };
    }

    const scope = summary.courseScope || {};
    const asIds = (value) => (Array.isArray(value) ? value : []).map(canonicalizeCourseId).filter(Boolean);

    // Prefer an entitled course, then fall back to one the account merely HAS an entry
    // for. The fallback is not cosmetic: it reproduces the old client, which selected
    // any subscription carrying a courseId when no entitled one matched. Without it a
    // lapsed user lands on a course-less screen instead of on their course showing a
    // renewal prompt.
    const candidate = asIds(scope.includedCourseIds)[0] || asIds(scope.excludedCourseIds)[0] || null;

    // A scope with no course ids at all (a legacy no-courseId subscriber) still yields
    // null, and the selection is left untouched — the old code found nothing for them
    // either, since it only ever looked at entries that had a courseId.
    return { subscriptionActive, courseIdToActivate: candidate };
}
